import {
  ArrowUpDown,
  GitMerge,
  Database,
  Sparkles,
  BookOpen,
  Globe,
  FileText,
  Terminal,
  User,
  Plug,
  Workflow,
  Bot,
  Puzzle,
  Table2,
} from "lucide-react";

/** Category presentation metadata. Node categories come from the backend. */
export type Category = {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  /** Tailwind text-* class — used for icon stroke (currentColor) and accents. */
  iconColor: string;
  /** Tailwind bg-* class — used for icon backdrop and header tint. */
  iconBg: string;
  /** Tailwind border-* class — used for handle dot borders and accents. */
  iconBorder: string;
};

export const CATEGORIES: Category[] = [
  {
    id: "core",
    label: "Core",
    description: "Inputs, outputs, and fixed values",
    icon: ArrowUpDown,
    iconColor: "text-gray-800",
    iconBg: "bg-gray-50",
    iconBorder: "border-gray-800",
  },
  {
    id: "flow",
    label: "Flow Control",
    description: "Conditions, loops, path merging, and debug",
    icon: GitMerge,
    iconColor: "text-slate-800",
    iconBg: "bg-slate-50",
    iconBorder: "border-slate-800",
  },
  {
    id: "data_ops",
    label: "Data Operations",
    description: "Transform and convert data",
    icon: Database,
    iconColor: "text-red-700",
    iconBg: "bg-red-50",
    iconBorder: "border-red-700",
  },
  {
    id: "ai",
    label: "AI & Generation",
    description: "Single-step AI calls and agents",
    icon: Sparkles,
    iconColor: "text-green-800",
    iconBg: "bg-green-50",
    iconBorder: "border-green-800",
  },
  {
    id: "knowledge",
    label: "Knowledge & Q&A",
    description: "The full RAG pipeline: ingestion, retrieval, answering",
    icon: BookOpen,
    iconColor: "text-blue-800",
    iconBg: "bg-blue-50",
    iconBorder: "border-blue-800",
  },
  {
    id: "tables",
    label: "Tables",
    description: "Read and write rows in workspace tables",
    icon: Table2,
    iconColor: "text-blue-800",
    iconBg: "bg-blue-50",
    iconBorder: "border-blue-800",
  },
  {
    id: "web",
    label: "Web & Research",
    description: "Anything that touches the open web",
    icon: Globe,
    iconColor: "text-cyan-800",
    iconBg: "bg-cyan-50",
    iconBorder: "border-cyan-800",
  },
  {
    id: "files",
    label: "Files & Documents",
    description: "Read, write, convert, and transform documents",
    icon: FileText,
    iconColor: "text-sky-800",
    iconBg: "bg-sky-50",
    iconBorder: "border-sky-800",
  },
  {
    id: "code",
    label: "APIs & Code",
    description: "HTTP requests, custom scripts, and secrets",
    icon: Terminal,
    iconColor: "text-fuchsia-800",
    iconBg: "bg-fuchsia-50",
    iconBorder: "border-fuchsia-800",
  },
  {
    id: "case_management",
    label: "Case Management",
    description: "Cases, tasks, and email pipelines",
    icon: User,
    iconColor: "text-purple-800",
    iconBg: "bg-purple-50",
    iconBorder: "border-purple-800",
  },
  {
    id: "integrations",
    label: "Integrations",
    description: "Connect to external services",
    icon: Plug,
    iconColor: "text-teal-800",
    iconBg: "bg-white",
    iconBorder: "border-teal-800",
  },
  {
    id: "agents",
    label: "Agents",
    description: "Run one of your saved agents",
    icon: Bot,
    iconColor: "text-emerald-800",
    iconBg: "bg-emerald-50",
    iconBorder: "border-emerald-800",
  },
  {
    id: "subflows",
    label: "Subflows",
    description: "Use other workflows as building blocks",
    icon: Workflow,
    iconColor: "text-slate-700",
    iconBg: "bg-slate-50",
    iconBorder: "border-slate-700",
  },
  {
    id: "plugins",
    label: "Plugins",
    description: "Nodes from your installed plugins",
    icon: Puzzle,
    iconColor: "text-indigo-700",
    iconBg: "bg-indigo-50",
    iconBorder: "border-indigo-700",
  },
];

export const TRAILING_PICKER_CATEGORY_IDS = new Set([
  "agents",
  "subflows",
  "plugins",
]);

const CATEGORY_BY_ID = new Map(CATEGORIES.map((c) => [c.id, c]));

/** Neutral fallback for nodes that don't match any category. */
export const DEFAULT_CATEGORY: Category = {
  id: "default",
  label: "Other",
  description: "",
  icon: ArrowUpDown,
  iconColor: "text-zinc-700",
  iconBg: "bg-zinc-50",
  iconBorder: "border-zinc-700",
};

type NodeLike = { type: string; category?: string | null };

export function nodeMatchesCategory(node: NodeLike, cat: Category): boolean {
  return (node.category || DEFAULT_CATEGORY.id) === cat.id;
}

export function getNodeCategory(node: NodeLike): Category {
  if (!node.category) return DEFAULT_CATEGORY;
  return (
    CATEGORY_BY_ID.get(node.category) ?? {
      ...DEFAULT_CATEGORY,
      id: node.category,
      label: node.category,
    }
  );
}

export function getPickerCategories(nodes: NodeLike[]): Category[] {
  const categories = new Map(
    nodes.map((node) => {
      const category = getNodeCategory(node);
      return [category.id, category];
    })
  );
  const known = CATEGORIES.filter(
    (category) =>
      categories.has(category.id) &&
      !TRAILING_PICKER_CATEGORY_IDS.has(category.id)
  );
  const unknown = [...categories.values()].filter(
    (category) => !CATEGORY_BY_ID.has(category.id)
  );
  const trailing = CATEGORIES.filter((category) =>
    TRAILING_PICKER_CATEGORY_IDS.has(category.id)
  );
  return [...known, ...unknown, ...trailing];
}

export type NodeColors = {
  /** Tailwind bg-* — header tint, icon backdrop. */
  bgClass: string;
  /** Tailwind text-* — icon stroke (via currentColor), label accents. */
  fgClass: string;
  /** Tailwind border-* — handle dots, outline accents. */
  borderClass: string;
};

/** Get the Tailwind color classes for a node based on its category. */
export function getNodeColors(node: NodeLike): NodeColors {
  const cat = getNodeCategory(node);
  return {
    bgClass: cat.iconBg,
    fgClass: cat.iconColor,
    borderClass: cat.iconBorder,
  };
}

const CATEGORY_BG_HEX: Record<string, string> = {
  "bg-gray-50": "#f9fafb",
  "bg-slate-50": "#f8fafc",
  "bg-red-50": "#fef2f2",
  "bg-green-50": "#f0fdf4",
  "bg-emerald-50": "#ecfdf5",
  "bg-blue-50": "#eff6ff",
  "bg-cyan-50": "#ecfeff",
  "bg-sky-50": "#f0f9ff",
  "bg-fuchsia-50": "#fdf4ff",
  "bg-purple-50": "#faf5ff",
  "bg-white": "#ffffff",
  "bg-zinc-50": "#fafafa",
};

/** Hex backdrop for inline chip/picker styles — mirrors getNodeColors().bgClass. */
export function getNodeIconBgHex(node: NodeLike): string {
  return CATEGORY_BG_HEX[getNodeColors(node).bgClass] ?? "#f9fafb";
}
